/**
 * The two period shapes room-web's pickers hold (docs/specs/user-preferences.md §2–§3, part 1a): their defaults,
 * their validation, what they send, how they are labelled, and the query-key discriminator each contributes. Pure and
 * React-free, so part 1b's preference registry imports these rather than restating them (UP26).
 *
 * The server is the control — `RoomPeriods` in `BrushPoker.Application` — and this is a mirror of it: what keeps a
 * picker from sending a value the server would refuse. They must agree, and periods.test.js reads the server's cap.
 *
 * The shapes are the registry's (§5.3): a Rundown period `{ kind, days? }`; an Overview period `{ kind: 'allTime' }`,
 * `{ preset }` or `{ from, to }`, exactly one form.
 */

/** The Tournaments list's presets, which the Overview offers; weeks run Sunday to Saturday at the room (UP9). */
export const PERIOD_PRESETS = [
  { id: 'yesterday', label: 'Yesterday', longestDays: 1 },
  { id: 'today', label: 'Today', longestDays: 1 },
  { id: 'tomorrow', label: 'Tomorrow', longestDays: 1 },
  { id: 'lastWeek', label: 'Last week', longestDays: 7 },
  { id: 'thisWeek', label: 'This week', longestDays: 7 },
  { id: 'nextWeek', label: 'Next week', longestDays: 7 },
  { id: 'lastMonth', label: 'Last month', longestDays: 31 },
  { id: 'thisMonth', label: 'This month', longestDays: 31 },
  { id: 'nextMonth', label: 'Next month', longestDays: 31 },
] as const;

export type PeriodPresetId = (typeof PERIOD_PRESETS)[number]['id'];

export type RundownKind = 'allUpcoming' | 'today' | 'nextDays' | 'thisWeek' | 'thisMonth';

/** A Rundown period: how far forward the scheduled part looks, from the room's day. */
export type RundownPeriod =
  | { kind: 'allUpcoming' }
  | { kind: 'today' }
  | { kind: 'nextDays'; days: number }
  | { kind: 'thisWeek' }
  | { kind: 'thisMonth' };

/** An Overview period: all time, a preset, or a custom range of both dates (yyyy-MM-dd). */
export type OverviewPeriod = { kind: 'allTime' } | { preset: PeriodPresetId } | { from: string; to: string };

/** The longest custom range, inclusive — the server's `RoomPeriods.MaxRangeDays`, set by the measurement gate (UP8). */
export const OVERVIEW_MAX_RANGE_DAYS = 31;

export const RUNDOWN_DAYS_MIN = 1;
export const RUNDOWN_DAYS_MAX = 90;
/** What the Next N days box starts at (UP23). */
export const RUNDOWN_DAYS_DEFAULT = 7;

/** The default Rundown period: today's behaviour, no parameter sent (UP15). */
export const DEFAULT_RUNDOWN: RundownPeriod = { kind: 'allUpcoming' };

/**
 * The default Overview totals period (UP24, A5): This month. The summary every screen shares therefore carries
 * `preset=thisMonth` from the first load; All time, which sends nothing, is a choice.
 */
export const DEFAULT_TOTALS: OverviewPeriod = { preset: 'thisMonth' };

/** The Rundown picker's choices, in the order offered (UP23). */
export const RUNDOWN_KINDS: readonly { kind: RundownKind; label: string }[] = [
  { kind: 'allUpcoming', label: 'All upcoming' },
  { kind: 'today', label: 'Today' },
  { kind: 'nextDays', label: 'Next N days' },
  { kind: 'thisWeek', label: 'This week' },
  { kind: 'thisMonth', label: 'This month' },
];

/**
 * Why the Next N days box's text is not a value the server takes, or null when it is: a whole number, digits only,
 * from 1 to 90 (UP18). Digits only because the server refuses `7.5`, `1e2` and a padded ` 7` as `invalid_days`.
 */
export function daysError(text: string): string | null {
  if (!/^-?\d+$/.test(text)) return `Enter a whole number of days, ${RUNDOWN_DAYS_MIN} to ${RUNDOWN_DAYS_MAX}.`;
  const n = Number(text);
  return n < RUNDOWN_DAYS_MIN || n > RUNDOWN_DAYS_MAX ? `Choose ${RUNDOWN_DAYS_MIN} to ${RUNDOWN_DAYS_MAX} days.` : null;
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The day number of a real yyyy-MM-dd date, or null — `2026-02-30` is not a date. */
function dayNumber(text: string): number | null {
  const m = ISO.exec(text);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  return back.getUTCFullYear() === y && back.getUTCMonth() === mo - 1 && back.getUTCDate() === d ? ms / 86_400_000 : null;
}

/** The presets offered at `cap`: a preset whose longest span exceeds it is hidden (UP24). */
export function overviewPresetOptions(cap: number = OVERVIEW_MAX_RANGE_DAYS) {
  return PERIOD_PRESETS.filter((p) => p.longestDays <= cap);
}

/**
 * Why an Overview period is not one the server takes, or null when it is (UP4–UP8): a preset it offers; or both
 * dates, real, running forwards, at most the cap. Checked before anything is sent, so the picker never sends a
 * range the server would refuse.
 */
export function overviewError(period: OverviewPeriod, cap: number = OVERVIEW_MAX_RANGE_DAYS): string | null {
  if ('kind' in period) return null;
  if ('preset' in period) {
    return overviewPresetOptions(cap).some((p) => p.id === period.preset) ? null : 'Choose one of the periods offered.';
  }
  if (!period.from || !period.to) return 'Choose both a start date and an end date.';
  const from = dayNumber(period.from);
  const to = dayNumber(period.to);
  if (from === null || to === null) return 'Enter the dates as real calendar dates.';
  if (to < from) return 'The end date is before the start date.';
  const span = to - from + 1;
  return span > cap ? `A custom range is at most ${cap} days; this one is ${span}.` : null;
}

function rundownParams(period: RundownPeriod): Record<string, string> {
  if (period.kind === 'allUpcoming') return {};
  return period.kind === 'nextDays' ? { rundown: 'nextDays', days: String(period.days) } : { rundown: period.kind };
}

function overviewParams(period: OverviewPeriod): Record<string, string> {
  if ('kind' in period) return {};
  return 'preset' in period ? { preset: period.preset } : { from: period.from, to: period.to };
}

/**
 * The summary's query: one request carries the Rundown period and the Overview totals period (UP22); All upcoming and
 * All time send nothing. The only read a period is sent on in 1a — room-web calls `/activity` with none (A5).
 */
export function toSummaryQuery(rundown: RundownPeriod, totals: OverviewPeriod): Record<string, string> {
  return { ...rundownParams(rundown), ...overviewParams(totals) };
}

/**
 * The Next N days stepper (UP23, A6): a whole-number field with an up and a down button and the arrow keys, from
 * RUNDOWN_DAYS_MIN to RUNDOWN_DAYS_MAX. A step starts from the period in force (`applied`), which is always valid, so
 * it never depends on whatever is typed in the field; it stops at the bounds rather than wrapping or clamping a step.
 */
export function rundownDaysSteps(applied: number): { up: boolean; down: boolean } {
  return { up: applied < RUNDOWN_DAYS_MAX, down: applied > RUNDOWN_DAYS_MIN };
}

/** The next value one step from `applied`, or null where the bound stops it (a disabled button, an inert key). */
export function stepRundownDays(applied: number, by: 1 | -1): number | null {
  const next = applied + by;
  return next < RUNDOWN_DAYS_MIN || next > RUNDOWN_DAYS_MAX ? null : next;
}

/** Which way an arrow key steps the field: up for ArrowUp, down for ArrowDown, nothing for any other key. */
export function stepForArrowKey(key: string): 1 | -1 | null {
  if (key === 'ArrowUp') return 1;
  return key === 'ArrowDown' ? -1 : null;
}

/** A typed value to apply, or null when it must not be applied (it keeps the last valid choice in force). */
export function typedRundownDays(text: string): number | null {
  return daysError(text) === null ? Number(text) : null;
}

/** What the field starts with: the days in force under Next N days, else RUNDOWN_DAYS_DEFAULT. */
export function rundownDaysText(period: RundownPeriod): string {
  return String(period.kind === 'nextDays' ? period.days : RUNDOWN_DAYS_DEFAULT);
}

/** The Overview totals period's name as Overview states it (UP24, A6): All time, a preset's name, or Custom range. */
export function totalsChoiceName(period: OverviewPeriod): string {
  if ('kind' in period) return 'All time';
  if ('preset' in period) return PERIOD_PRESETS.find((p) => p.id === period.preset)?.label ?? period.preset;
  return 'Custom range';
}

export function rundownLabel(period: RundownPeriod): string {
  if (period.kind === 'nextDays') return `Next ${period.days} day${period.days === 1 ? '' : 's'}`;
  return RUNDOWN_KINDS.find((k) => k.kind === period.kind)?.label ?? period.kind;
}

/** A date with a named month, never `03/04/2026`, which is two days depending on the reader. */
const RANGE_DAY = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** A yyyy-MM-dd date with a named month. */
export function periodDate(iso: string): string {
  const n = dayNumber(iso);
  return n === null ? iso : RANGE_DAY.format(new Date(n * 86_400_000));
}

export function overviewLabel(period: OverviewPeriod): string {
  if ('kind' in period) return 'All time';
  if ('preset' in period) return PERIOD_PRESETS.find((p) => p.id === period.preset)?.label ?? period.preset;
  return `${periodDate(period.from)} – ${periodDate(period.to)}`;
}

/** The Rundown period's query-key discriminator: equal for equal choices (UP22). */
export function rundownKey(period: RundownPeriod): string {
  return period.kind === 'nextDays' ? `nextDays:${period.days}` : period.kind;
}

/** The Overview period's query-key discriminator: equal for equal choices (UP22). */
export function overviewKey(period: OverviewPeriod): string {
  if ('kind' in period) return 'allTime';
  return 'preset' in period ? `preset:${period.preset}` : `range:${period.from}:${period.to}`;
}
